import type { Algorithm, PipelineStage, Problem } from '../types'

// ---------------------------------------------------------------------------
// Shared pipeline scaffold. Every model teaches the same 6-stage anatomy so a
// student learns the *shape* of an ML solution once, then sees how each stage
// changes per problem/algorithm. Code here is illustrative (mocked) — the real
// FastAPI backend will serve the exact training scripts later.
// ---------------------------------------------------------------------------

const WHY = {
  dataset:
    'Everything starts with data. This stage loads the raw records and prints their shape — rows and columns are the first signal of whether the problem is even learnable.',
  preprocess:
    'Real data is messy. We fill missing values, turn categorical text into numbers the model can read, and split into train/test so our evaluation is honest.',
  features:
    "A model doesn't see 'a customer' — it sees numbers. Feature engineering turns raw columns into signals (ratios, aggregates, encodings) that actually carry predictive power.",
  model:
    'Pick the learner. This is the stage that differs between the leaves on the same problem — same question, different algorithm, different trade-offs.',
  train:
    'Fit on the training set, then measure on data the model never saw. The metric printed here is the number you would defend to a stakeholder.',
  predict:
    'The payoff. Feed one new example in and get a decision out — this is exactly what executes when you press Run.',
}

interface StageCfg {
  dataset: string
  preprocess?: string
  features: string
  model: string
  train?: string
  predict: string
}

const PRE_CLF = `from sklearn.model_selection import train_test_split
import pandas as pd

df = df.fillna(df.median(numeric_only=True))
df = pd.get_dummies(df, drop_first=True)   # encode categoricals

X, y = df.drop(columns=[TARGET]), df[TARGET]
X_train, X_test, y_train, y_test = train_test_split(
    X, y, test_size=0.2, random_state=42, stratify=y)`

const PRE_REG = `from sklearn.model_selection import train_test_split
import pandas as pd

df = df.fillna(df.median(numeric_only=True))
df = pd.get_dummies(df, drop_first=True)   # encode categoricals

X, y = df.drop(columns=[TARGET]), df[TARGET]
X_train, X_test, y_train, y_test = train_test_split(
    X, y, test_size=0.2, random_state=42)`

const TRAIN_CLF = `from sklearn.metrics import roc_auc_score, accuracy_score

model.fit(X_train, y_train)
proba = model.predict_proba(X_test)[:, 1]
print(f"AUC {roc_auc_score(y_test, proba):.3f}")
print(f"accuracy {accuracy_score(y_test, model.predict(X_test)):.3f}")`

const TRAIN_REG = `from sklearn.metrics import mean_absolute_error, r2_score

model.fit(X_train, y_train)
pred = model.predict(X_test)
print(f"MAE {mean_absolute_error(y_test, pred):,.1f}")
print(f"R2  {r2_score(y_test, pred):.3f}")`

function buildStages(prefix: string, cfg: StageCfg, kind: 'clf' | 'reg'): PipelineStage[] {
  return [
    { id: `${prefix}-dataset`, label: 'Dataset', why: WHY.dataset, code: cfg.dataset, language: 'python' },
    { id: `${prefix}-preprocess`, label: 'Preprocess', why: WHY.preprocess, code: cfg.preprocess ?? (kind === 'clf' ? PRE_CLF : PRE_REG), language: 'python' },
    { id: `${prefix}-features`, label: 'Features', why: WHY.features, code: cfg.features, language: 'python' },
    { id: `${prefix}-model`, label: 'Model', why: WHY.model, code: cfg.model, language: 'python' },
    { id: `${prefix}-train`, label: 'Train & Eval', why: WHY.train, code: cfg.train ?? (kind === 'clf' ? TRAIN_CLF : TRAIN_REG), language: 'python' },
    { id: `${prefix}-predict`, label: 'Predict', why: WHY.predict, code: cfg.predict, language: 'python' },
  ]
}

// ---------------------------------------------------------------------------
// The 10 problems
// ---------------------------------------------------------------------------

export const PROBLEMS: Problem[] = [
  // 1 — CHURN -----------------------------------------------------------------
  {
    id: 'churn',
    title: 'Predict Customer Churn',
    category: 'Classification · Tabular',
    blurb: 'Will this customer leave the service?',
    realWorld: 'Telecom, SaaS and banks save millions by catching at-risk customers before they cancel.',
    algorithms: [
      {
        id: 'churn-xgb',
        name: 'XGBoost',
        summary: 'Gradient-boosted trees — the workhorse for tabular business data.',
        dataset: { name: 'Telco Churn (Kaggle)', rows: 7043, features: 19 },
        sampleMetric: { name: 'AUC', value: '0.87' },
        output: { kind: 'classification', positiveLabel: 'Will churn', negativeLabel: 'Will stay' },
        inputs: [
          { name: 'tenure', label: 'Tenure (months)', type: 'number', default: 5 },
          { name: 'monthly_charges', label: 'Monthly charges', type: 'number', default: 84, unit: '$' },
          { name: 'contract', label: 'Contract', type: 'select', default: 'Month-to-month', options: ['Month-to-month', 'One year', 'Two year'] },
          { name: 'support_calls', label: 'Support calls (90d)', type: 'number', default: 4 },
        ],
        featureImportance: [
          { name: 'contract', value: 0.34 },
          { name: 'tenure', value: 0.27 },
          { name: 'support_calls', value: 0.21 },
          { name: 'monthly_charges', value: 0.18 },
        ],
        pipeline: buildStages('churn-xgb', {
          dataset: `import pandas as pd
df = pd.read_csv("telco_churn.csv")
TARGET = "Churn"
print(df.shape)          # (7043, 20)
print(df[TARGET].value_counts(normalize=True))`,
          features: `# tenure buckets + charge ratios carry most of the signal
df["charges_per_month_tenure"] = df["MonthlyCharges"] / (df["tenure"] + 1)
df["is_month_to_month"] = (df["Contract"] == "Month-to-month").astype(int)
df["high_support"] = (df["support_calls"] > 3).astype(int)`,
          model: `from xgboost import XGBClassifier
model = XGBClassifier(
    n_estimators=400, max_depth=5, learning_rate=0.05,
    subsample=0.9, eval_metric="auc")`,
          predict: `row = {"tenure": 5, "MonthlyCharges": 84,
       "Contract": "Month-to-month", "support_calls": 4}
p = model.predict_proba(encode(row))[0, 1]
print(f"churn probability: {p:.0%}")`,
        }, 'clf'),
      },
      {
        id: 'churn-rf',
        name: 'Random Forest',
        summary: 'Bagged trees — robust, hard to overfit, great baseline.',
        dataset: { name: 'Telco Churn (Kaggle)', rows: 7043, features: 19 },
        sampleMetric: { name: 'AUC', value: '0.84' },
        output: { kind: 'classification', positiveLabel: 'Will churn', negativeLabel: 'Will stay' },
        inputs: [
          { name: 'tenure', label: 'Tenure (months)', type: 'number', default: 5 },
          { name: 'monthly_charges', label: 'Monthly charges', type: 'number', default: 84, unit: '$' },
          { name: 'contract', label: 'Contract', type: 'select', default: 'Month-to-month', options: ['Month-to-month', 'One year', 'Two year'] },
          { name: 'support_calls', label: 'Support calls (90d)', type: 'number', default: 4 },
        ],
        featureImportance: [
          { name: 'tenure', value: 0.31 },
          { name: 'contract', value: 0.29 },
          { name: 'monthly_charges', value: 0.22 },
          { name: 'support_calls', value: 0.18 },
        ],
        pipeline: buildStages('churn-rf', {
          dataset: `import pandas as pd
df = pd.read_csv("telco_churn.csv")
TARGET = "Churn"
print(df.shape)`,
          features: `df["charges_per_month_tenure"] = df["MonthlyCharges"] / (df["tenure"] + 1)
df["is_month_to_month"] = (df["Contract"] == "Month-to-month").astype(int)`,
          model: `from sklearn.ensemble import RandomForestClassifier
model = RandomForestClassifier(
    n_estimators=500, max_depth=None,
    min_samples_leaf=3, n_jobs=-1, random_state=42)`,
          predict: `p = model.predict_proba(encode(row))[0, 1]
print(f"churn probability: {p:.0%}")`,
        }, 'clf'),
      },
    ],
  },

  // 2 — FRAUD -----------------------------------------------------------------
  {
    id: 'fraud',
    title: 'Fraud Detection',
    category: 'Anomaly · Tabular',
    blurb: 'Is this transaction suspicious?',
    realWorld: 'Banks, insurers and payment gateways flag anomalies over streams of numeric transactions.',
    algorithms: [
      {
        id: 'fraud-iso',
        name: 'Isolation Forest',
        summary: 'Unsupervised anomaly detection — isolates rare points fast.',
        dataset: { name: 'Credit Card Fraud (ULB)', rows: 284807, features: 30 },
        sampleMetric: { name: 'Recall@1%', value: '0.82' },
        output: { kind: 'classification', positiveLabel: 'Fraud', negativeLabel: 'Legit' },
        inputs: [
          { name: 'amount', label: 'Amount', type: 'number', default: 1200, unit: '$' },
          { name: 'txn_hour', label: 'Hour of day', type: 'number', default: 3 },
          { name: 'foreign', label: 'Foreign card?', type: 'select', default: 'yes', options: ['yes', 'no'] },
          { name: 'velocity', label: 'Txns last hour', type: 'number', default: 6 },
        ],
        featureImportance: [
          { name: 'amount', value: 0.3 },
          { name: 'velocity', value: 0.28 },
          { name: 'txn_hour', value: 0.24 },
          { name: 'foreign', value: 0.18 },
        ],
        pipeline: buildStages('fraud-iso', {
          dataset: `import pandas as pd
df = pd.read_csv("creditcard.csv")
print(df.shape)                       # (284807, 31)
print("fraud rate:", df.Class.mean()) # ~0.0017 — highly imbalanced`,
          preprocess: `from sklearn.preprocessing import RobustScaler
# no labels needed for training an isolation forest
X = df.drop(columns=["Class"])
X = RobustScaler().fit_transform(X)`,
          features: `# engineered behavioural signals matter more than raw amount
df["amount_z"] = (df.Amount - df.Amount.mean()) / df.Amount.std()
df["is_night"] = df.txn_hour.between(0, 5).astype(int)`,
          model: `from sklearn.ensemble import IsolationForest
model = IsolationForest(
    n_estimators=300, contamination=0.002, random_state=42)`,
          train: `model.fit(X)
scores = -model.score_samples(X)   # higher = more anomalous
threshold = np.quantile(scores, 0.99)
print(f"flagging anything above {threshold:.3f}")`,
          predict: `s = -model.score_samples(encode(row))[0]
print("FRAUD" if s > threshold else "legit", f"(score {s:.2f})")`,
        }, 'clf'),
      },
      {
        id: 'fraud-xgb',
        name: 'XGBoost',
        summary: 'Supervised boosting when you have labelled fraud history.',
        dataset: { name: 'Credit Card Fraud (ULB)', rows: 284807, features: 30 },
        sampleMetric: { name: 'AUC', value: '0.98' },
        output: { kind: 'classification', positiveLabel: 'Fraud', negativeLabel: 'Legit' },
        inputs: [
          { name: 'amount', label: 'Amount', type: 'number', default: 1200, unit: '$' },
          { name: 'txn_hour', label: 'Hour of day', type: 'number', default: 3 },
          { name: 'foreign', label: 'Foreign card?', type: 'select', default: 'yes', options: ['yes', 'no'] },
          { name: 'velocity', label: 'Txns last hour', type: 'number', default: 6 },
        ],
        featureImportance: [
          { name: 'velocity', value: 0.33 },
          { name: 'amount', value: 0.27 },
          { name: 'foreign', value: 0.22 },
          { name: 'txn_hour', value: 0.18 },
        ],
        pipeline: buildStages('fraud-xgb', {
          dataset: `import pandas as pd
df = pd.read_csv("creditcard.csv")
TARGET = "Class"
print("fraud rate:", df[TARGET].mean())`,
          features: `df["amount_z"] = (df.Amount - df.Amount.mean()) / df.Amount.std()
df["is_night"] = df.txn_hour.between(0, 5).astype(int)`,
          model: `from xgboost import XGBClassifier
model = XGBClassifier(
    n_estimators=600, max_depth=6, learning_rate=0.03,
    scale_pos_weight=580,   # counter the class imbalance
    eval_metric="aucpr")`,
          predict: `p = model.predict_proba(encode(row))[0, 1]
print(f"fraud probability: {p:.1%}")`,
        }, 'clf'),
      },
    ],
  },

  // 3 — EQUIPMENT FAILURE ------------------------------------------------------
  {
    id: 'equipment',
    title: 'Equipment Failure Prediction',
    category: 'Classification · Sensors',
    blurb: 'Will this machine break down soon?',
    realWorld: 'Factories and Industry 4.0 lines schedule maintenance before costly breakdowns.',
    algorithms: [
      {
        id: 'equip-rf',
        name: 'Random Forest',
        summary: 'Handles noisy sensor mixes without heavy tuning.',
        dataset: { name: 'AI4I Predictive Maintenance', rows: 10000, features: 8 },
        sampleMetric: { name: 'F1', value: '0.91' },
        output: { kind: 'classification', positiveLabel: 'Will fail soon', negativeLabel: 'Healthy' },
        inputs: [
          { name: 'air_temp', label: 'Air temp (K)', type: 'number', default: 300 },
          { name: 'torque', label: 'Torque (Nm)', type: 'number', default: 65 },
          { name: 'rpm', label: 'Rotational speed (rpm)', type: 'number', default: 1400 },
          { name: 'tool_wear', label: 'Tool wear (min)', type: 'number', default: 210 },
        ],
        featureImportance: [
          { name: 'tool_wear', value: 0.36 },
          { name: 'torque', value: 0.29 },
          { name: 'rpm', value: 0.2 },
          { name: 'air_temp', value: 0.15 },
        ],
        pipeline: buildStages('equip-rf', {
          dataset: `import pandas as pd
df = pd.read_csv("ai4i2020.csv")
TARGET = "Machine failure"
print(df.shape)   # (10000, 14)`,
          features: `# physics-informed features beat raw sensor values
df["power"] = df["Torque [Nm]"] * df["Rotational speed [rpm]"]
df["temp_diff"] = df["Process temperature [K]"] - df["Air temperature [K]"]
df["wear_rate"] = df["Tool wear [min]"] / (df["Rotational speed [rpm]"] + 1)`,
          model: `from sklearn.ensemble import RandomForestClassifier
model = RandomForestClassifier(
    n_estimators=400, class_weight="balanced",
    min_samples_leaf=2, n_jobs=-1, random_state=42)`,
          predict: `p = model.predict_proba(encode(row))[0, 1]
print("MAINTENANCE DUE" if p > 0.5 else "healthy", f"({p:.0%})")`,
        }, 'clf'),
      },
    ],
  },

  // 4 — NETWORK INTRUSION ------------------------------------------------------
  {
    id: 'intrusion',
    title: 'Network Intrusion Detection',
    category: 'Classification · Security',
    blurb: 'Is this network traffic an attack?',
    realWorld: 'SOC teams detect cyber attacks from packet-level patterns, not language.',
    algorithms: [
      {
        id: 'intrusion-gb',
        name: 'Gradient Boosting',
        summary: 'Strong on the mixed numeric/categorical NSL-KDD features.',
        dataset: { name: 'NSL-KDD', rows: 125973, features: 41 },
        sampleMetric: { name: 'Accuracy', value: '0.94' },
        output: { kind: 'classification', positiveLabel: 'Attack', negativeLabel: 'Benign' },
        inputs: [
          { name: 'src_bytes', label: 'Source bytes', type: 'number', default: 0 },
          { name: 'dst_bytes', label: 'Dest bytes', type: 'number', default: 0 },
          { name: 'count', label: 'Conn. count', type: 'number', default: 500 },
          { name: 'service', label: 'Service', type: 'select', default: 'private', options: ['http', 'private', 'ftp', 'smtp'] },
        ],
        featureImportance: [
          { name: 'count', value: 0.32 },
          { name: 'src_bytes', value: 0.26 },
          { name: 'service', value: 0.23 },
          { name: 'dst_bytes', value: 0.19 },
        ],
        pipeline: buildStages('intrusion-gb', {
          dataset: `import pandas as pd
df = pd.read_csv("KDDTrain+.csv", names=COLS)
df["attack"] = (df["label"] != "normal").astype(int)
TARGET = "attack"`,
          features: `# connection-rate features expose scans and floods
df["bytes_ratio"] = df.src_bytes / (df.dst_bytes + 1)
df["error_rate"] = df.serror_rate + df.rerror_rate`,
          model: `from sklearn.ensemble import GradientBoostingClassifier
model = GradientBoostingClassifier(
    n_estimators=300, max_depth=3, learning_rate=0.1)`,
          predict: `p = model.predict_proba(encode(row))[0, 1]
print("ATTACK" if p > 0.5 else "benign", f"({p:.0%})")`,
        }, 'clf'),
      },
    ],
  },

  // 5 — DEMAND FORECASTING -----------------------------------------------------
  {
    id: 'demand',
    title: 'Demand Forecasting',
    category: 'Regression · Time series',
    blurb: 'How many units will sell next week?',
    realWorld: 'Retail and supply chains optimise inventory — too much ties up cash, too little loses sales.',
    algorithms: [
      {
        id: 'demand-xgb',
        name: 'XGBoost',
        summary: 'Lag + calendar features turn forecasting into supervised regression.',
        dataset: { name: 'Store Item Demand', rows: 913000, features: 12 },
        sampleMetric: { name: 'MAPE', value: '9.4%' },
        output: { kind: 'regression', unit: 'units', base: 420, scale: 60 },
        inputs: [
          { name: 'last_week', label: 'Last week sales', type: 'number', default: 430, unit: 'units' },
          { name: 'lag_52', label: 'Same week last year', type: 'number', default: 410, unit: 'units' },
          { name: 'promo', label: 'Promotion on?', type: 'select', default: 'no', options: ['no', 'yes'] },
          { name: 'week_of_year', label: 'Week of year', type: 'number', default: 48 },
        ],
        featureImportance: [
          { name: 'last_week', value: 0.41 },
          { name: 'lag_52', value: 0.24 },
          { name: 'week_of_year', value: 0.2 },
          { name: 'promo', value: 0.15 },
        ],
        pipeline: buildStages('demand-xgb', {
          dataset: `import pandas as pd
df = pd.read_csv("sales.csv", parse_dates=["date"])
df = df.sort_values("date")
TARGET = "sales"`,
          preprocess: `# time series: split by TIME, never shuffle
cut = df["date"].quantile(0.8)
train, test = df[df.date <= cut], df[df.date > cut]`,
          features: `for lag in [1, 7, 52]:
    df[f"lag_{lag}"] = df.groupby("store_item")["sales"].shift(lag)
df["week_of_year"] = df.date.dt.isocalendar().week
df["rolling_4"] = df.groupby("store_item")["sales"].shift(1).rolling(4).mean()`,
          model: `from xgboost import XGBRegressor
model = XGBRegressor(
    n_estimators=800, max_depth=6, learning_rate=0.03,
    subsample=0.8, objective="reg:squarederror")`,
          train: TRAIN_REG,
          predict: `yhat = model.predict(encode(row))[0]
print(f"forecast next week: {yhat:,.0f} units")`,
        }, 'reg'),
      },
    ],
  },

  // 6 — RECOMMENDATION ---------------------------------------------------------
  {
    id: 'recommend',
    title: 'Recommendation Engine',
    category: 'Regression · Embeddings',
    blurb: 'How will this user rate this item?',
    realWorld: 'Netflix, Amazon and Spotify learn latent taste vectors — the gateway to embeddings.',
    algorithms: [
      {
        id: 'recommend-mf',
        name: 'Matrix Factorization',
        summary: 'Learn user & item embeddings whose dot product predicts the rating.',
        dataset: { name: 'MovieLens 100K', rows: 100000, features: 2 },
        sampleMetric: { name: 'RMSE', value: '0.91' },
        output: { kind: 'regression', unit: '★', base: 3.4, scale: 0.8 },
        inputs: [
          { name: 'user_avg', label: 'User avg rating', type: 'number', default: 3.8, unit: '★' },
          { name: 'item_avg', label: 'Item avg rating', type: 'number', default: 4.1, unit: '★' },
          { name: 'genre_match', label: 'Genre affinity (0-1)', type: 'number', default: 0.7 },
          { name: 'n_rated', label: 'User # ratings', type: 'number', default: 120 },
        ],
        featureImportance: [
          { name: 'item_avg', value: 0.34 },
          { name: 'genre_match', value: 0.3 },
          { name: 'user_avg', value: 0.24 },
          { name: 'n_rated', value: 0.12 },
        ],
        pipeline: buildStages('recommend-mf', {
          dataset: `import pandas as pd
ratings = pd.read_csv("ml-100k/u.data", sep="\\t",
    names=["user", "item", "rating", "ts"])
print(ratings.shape)   # (100000, 4)`,
          preprocess: `from sklearn.model_selection import train_test_split
train, test = train_test_split(ratings, test_size=0.2, random_state=42)
n_users = ratings.user.nunique()
n_items = ratings.item.nunique()`,
          features: `# no hand features — the model LEARNS latent factors
K = 32   # embedding dimension per user & item`,
          model: `import numpy as np
# P: user factors, Q: item factors — trained by SGD
P = np.random.normal(scale=0.1, size=(n_users, K))
Q = np.random.normal(scale=0.1, size=(n_items, K))`,
          train: `for epoch in range(20):
    for u, i, r in train_triples:
        err = r - P[u] @ Q[i]
        P[u] += lr * (err * Q[i] - reg * P[u])
        Q[i] += lr * (err * P[u] - reg * Q[i])
print(f"RMSE {rmse(test):.3f}")`,
          predict: `pred = P[user] @ Q[item]
print(f"predicted rating: {pred:.1f} / 5")`,
        }, 'reg'),
      },
    ],
  },

  // 7 — HOUSE PRICE ------------------------------------------------------------
  {
    id: 'house',
    title: 'House Price Prediction',
    category: 'Regression · Tabular',
    blurb: 'What is this house worth?',
    realWorld: 'The classic beginner project — visual, intuitive, and all about feature engineering.',
    algorithms: [
      {
        id: 'house-xgb',
        name: 'XGBoost',
        summary: 'Captures non-linear interactions between size, location and quality.',
        dataset: { name: 'Ames Housing', rows: 1460, features: 79 },
        sampleMetric: { name: 'R²', value: '0.91' },
        output: { kind: 'regression', unit: '$', base: 180000, scale: 45000 },
        inputs: [
          { name: 'living_area', label: 'Living area (sqft)', type: 'number', default: 1800 },
          { name: 'overall_qual', label: 'Overall quality (1-10)', type: 'number', default: 7 },
          { name: 'year_built', label: 'Year built', type: 'number', default: 2005 },
          { name: 'garage_cars', label: 'Garage (cars)', type: 'number', default: 2 },
        ],
        featureImportance: [
          { name: 'overall_qual', value: 0.38 },
          { name: 'living_area', value: 0.31 },
          { name: 'year_built', value: 0.18 },
          { name: 'garage_cars', value: 0.13 },
        ],
        pipeline: buildStages('house-xgb', {
          dataset: `import pandas as pd
df = pd.read_csv("ames_housing.csv")
TARGET = "SalePrice"
print(df.shape)                 # (1460, 81)
print(df[TARGET].describe())`,
          features: `# combine correlated columns into stronger signals
df["total_sf"] = df["1stFlrSF"] + df["2ndFlrSF"] + df["TotalBsmtSF"]
df["age"] = df["YrSold"] - df["YearBuilt"]
df["qual_area"] = df["OverallQual"] * df["GrLivArea"]`,
          model: `from xgboost import XGBRegressor
model = XGBRegressor(
    n_estimators=600, max_depth=4, learning_rate=0.05,
    subsample=0.8, colsample_bytree=0.8)`,
          train: TRAIN_REG,
          predict: `row = {"GrLivArea": 1800, "OverallQual": 7,
       "YearBuilt": 2005, "GarageCars": 2}
price = model.predict(encode(row))[0]
print(f"estimated price: \${price:,.0f}")`,
        }, 'reg'),
      },
    ],
  },

  // 8 — CREDIT RISK ------------------------------------------------------------
  {
    id: 'credit',
    title: 'Credit Risk Scoring',
    category: 'Classification · Finance',
    blurb: 'Will this borrower default?',
    realWorld: 'Banks and fintechs price loans and approve applications — and must stay explainable.',
    algorithms: [
      {
        id: 'credit-lr',
        name: 'Logistic Regression',
        summary: 'Transparent, regulator-friendly — coefficients you can defend.',
        dataset: { name: 'German Credit', rows: 1000, features: 20 },
        sampleMetric: { name: 'AUC', value: '0.79' },
        output: { kind: 'classification', positiveLabel: 'Default', negativeLabel: 'Repaid' },
        inputs: [
          { name: 'income', label: 'Monthly income', type: 'number', default: 3200, unit: '$' },
          { name: 'loan_amount', label: 'Loan amount', type: 'number', default: 24000, unit: '$' },
          { name: 'credit_history', label: 'Credit history', type: 'select', default: 'delayed', options: ['excellent', 'good', 'delayed', 'critical'] },
          { name: 'employment_years', label: 'Employment (years)', type: 'number', default: 2 },
        ],
        featureImportance: [
          { name: 'credit_history', value: 0.35 },
          { name: 'loan_amount', value: 0.27 },
          { name: 'income', value: 0.24 },
          { name: 'employment_years', value: 0.14 },
        ],
        pipeline: buildStages('credit-lr', {
          dataset: `import pandas as pd
df = pd.read_csv("german_credit.csv")
TARGET = "default"
print(df[TARGET].value_counts())`,
          features: `df["debt_to_income"] = df.loan_amount / (df.income * 12)
df["loan_per_year_emp"] = df.loan_amount / (df.employment_years + 1)`,
          model: `from sklearn.linear_model import LogisticRegression
model = LogisticRegression(
    C=0.5, class_weight="balanced", max_iter=1000)`,
          predict: `p = model.predict_proba(encode(row))[0, 1]
print("DECLINE" if p > 0.5 else "approve", f"(default risk {p:.0%})")`,
        }, 'clf'),
      },
      {
        id: 'credit-xgb',
        name: 'XGBoost',
        summary: 'Higher accuracy when explainability can lean on SHAP.',
        dataset: { name: 'German Credit', rows: 1000, features: 20 },
        sampleMetric: { name: 'AUC', value: '0.83' },
        output: { kind: 'classification', positiveLabel: 'Default', negativeLabel: 'Repaid' },
        inputs: [
          { name: 'income', label: 'Monthly income', type: 'number', default: 3200, unit: '$' },
          { name: 'loan_amount', label: 'Loan amount', type: 'number', default: 24000, unit: '$' },
          { name: 'credit_history', label: 'Credit history', type: 'select', default: 'delayed', options: ['excellent', 'good', 'delayed', 'critical'] },
          { name: 'employment_years', label: 'Employment (years)', type: 'number', default: 2 },
        ],
        featureImportance: [
          { name: 'credit_history', value: 0.32 },
          { name: 'debt_to_income', value: 0.3 },
          { name: 'income', value: 0.22 },
          { name: 'employment_years', value: 0.16 },
        ],
        pipeline: buildStages('credit-xgb', {
          dataset: `import pandas as pd
df = pd.read_csv("german_credit.csv")
TARGET = "default"`,
          features: `df["debt_to_income"] = df.loan_amount / (df.income * 12)`,
          model: `from xgboost import XGBClassifier
model = XGBClassifier(
    n_estimators=300, max_depth=4, learning_rate=0.05,
    scale_pos_weight=2.3)`,
          predict: `p = model.predict_proba(encode(row))[0, 1]
print(f"default risk: {p:.0%}")`,
        }, 'clf'),
      },
    ],
  },

  // 9 — CROP DISEASE -----------------------------------------------------------
  {
    id: 'crop',
    title: 'Crop Disease Detection',
    category: 'Computer Vision · CNN',
    blurb: 'What disease is on this leaf?',
    realWorld: 'Farmers photograph a leaf and get a diagnosis — trainable in hours on a laptop.',
    algorithms: [
      {
        id: 'crop-cnn',
        name: 'MobileNet CNN',
        summary: 'Transfer learning — a small pretrained net fine-tuned on leaf images.',
        dataset: { name: 'PlantVillage', rows: 54305, features: 224 },
        sampleMetric: { name: 'Top-1 acc', value: '0.97' },
        output: { kind: 'image_class' },
        inputs: [
          {
            name: 'sample_image',
            label: 'Sample leaf image',
            type: 'select',
            default: 'Tomato — Early Blight',
            options: ['Healthy', 'Tomato — Early Blight', 'Tomato — Late Blight', 'Tomato — Leaf Mold', 'Potato — Late Blight'],
          },
        ],
        featureImportance: [
          { name: 'lesion texture', value: 0.4 },
          { name: 'leaf colour', value: 0.33 },
          { name: 'edge shape', value: 0.27 },
        ],
        pipeline: buildStages('crop-cnn', {
          dataset: `import tensorflow as tf
ds = tf.keras.utils.image_dataset_from_directory(
    "PlantVillage", image_size=(224, 224), batch_size=32)
print(ds.class_names)   # 38 disease classes`,
          preprocess: `# scale pixels + augment to fight overfitting
norm = tf.keras.layers.Rescaling(1./255)
augment = tf.keras.Sequential([
    tf.keras.layers.RandomFlip("horizontal"),
    tf.keras.layers.RandomRotation(0.1)])`,
          features: `# a CNN LEARNS its own features from pixels —
# early layers detect edges, deeper layers detect lesions`,
          model: `base = tf.keras.applications.MobileNetV2(
    input_shape=(224, 224, 3), include_top=False, weights="imagenet")
base.trainable = False   # transfer learning
model = tf.keras.Sequential([
    base, tf.keras.layers.GlobalAveragePooling2D(),
    tf.keras.layers.Dense(38, activation="softmax")])`,
          train: `model.compile(optimizer="adam",
    loss="sparse_categorical_crossentropy", metrics=["accuracy"])
model.fit(train_ds, validation_data=val_ds, epochs=5)
# export for fast CPU inference
tf.saved_model.save(model, "crop_model"); to_onnx("crop_model")`,
          predict: `img = load_image("leaf.jpg")            # 224x224x3
probs = session.run(None, {"input": img})[0][0]
cls = class_names[probs.argmax()]
print(f"{cls}  ({probs.max():.0%} confident)")`,
        }, 'clf'),
      },
    ],
  },

  // 10 — ENERGY FORECASTING ----------------------------------------------------
  {
    id: 'energy',
    title: 'Energy Consumption Forecasting',
    category: 'Regression · Time series',
    blurb: 'How much electricity will be used?',
    realWorld: 'Smart grids and buildings balance load and cost by predicting demand ahead of time.',
    algorithms: [
      {
        id: 'energy-xgb',
        name: 'XGBoost',
        summary: 'Weather + calendar + lag features predict hourly load.',
        dataset: { name: 'PJM Hourly Load', rows: 145366, features: 14 },
        sampleMetric: { name: 'MAPE', value: '4.1%' },
        output: { kind: 'regression', unit: 'MW', base: 32000, scale: 5000 },
        inputs: [
          { name: 'temp', label: 'Temperature (°C)', type: 'number', default: 34 },
          { name: 'hour', label: 'Hour of day', type: 'number', default: 18 },
          { name: 'is_weekend', label: 'Weekend?', type: 'select', default: 'no', options: ['no', 'yes'] },
          { name: 'load_lag_24', label: 'Load 24h ago (MW)', type: 'number', default: 31000 },
        ],
        featureImportance: [
          { name: 'load_lag_24', value: 0.37 },
          { name: 'temp', value: 0.28 },
          { name: 'hour', value: 0.22 },
          { name: 'is_weekend', value: 0.13 },
        ],
        pipeline: buildStages('energy-xgb', {
          dataset: `import pandas as pd
df = pd.read_csv("pjm_hourly.csv", parse_dates=["Datetime"])
df = df.sort_values("Datetime")
TARGET = "MW"`,
          preprocess: `cut = df.Datetime.quantile(0.8)
train, test = df[df.Datetime <= cut], df[df.Datetime > cut]`,
          features: `df["hour"] = df.Datetime.dt.hour
df["dow"] = df.Datetime.dt.dayofweek
df["is_weekend"] = (df.dow >= 5).astype(int)
for lag in [24, 168]:            # yesterday, last week
    df[f"lag_{lag}"] = df.MW.shift(lag)`,
          model: `from xgboost import XGBRegressor
model = XGBRegressor(
    n_estimators=1000, max_depth=7, learning_rate=0.02,
    subsample=0.8)`,
          train: TRAIN_REG,
          predict: `yhat = model.predict(encode(row))[0]
print(f"predicted load: {yhat:,.0f} MW")`,
        }, 'reg'),
      },
    ],
  },
]

// flat lookup helpers -------------------------------------------------------
export const ALGO_INDEX: Record<string, { problem: Problem; algo: Algorithm }> = {}
for (const problem of PROBLEMS) {
  for (const algo of problem.algorithms) {
    ALGO_INDEX[algo.id] = { problem, algo }
  }
}
